import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, Plus, UserCircle2, Filter, CalendarClock, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { CAT_COLOR } from "@/lib/constants";
import { useStatuses } from "@/context/StatusContext";
import { fmtDate } from "@/lib/time";
import { PrioritaBadge, StatoBadge } from "@/components/Badges";

const START_HOUR = 7, END_HOUR = 20;
const TOTAL_MIN = (END_HOUR - START_HOUR) * 60;
const PX_PER_MIN = 1.5, ROW_H = 138, SNAP = 15;
const LANE_W = TOTAL_MIN * PX_PER_MIN;
const OVERLAP_OFFSET = 16;
const BAND_H = 30; // altezza della "bandella" visibile per i ticket sovrapposti (cascata verticale)

// Assegna a ciascun tile una "colonna" di sovrapposizione (0 = nessuna sovrapposizione a sinistra)
function stackCols(items) {
  const sorted = [...items].sort((a, b) => a._min - b._min || a._dur - b._dur);
  const active = [];
  const res = {};
  for (const it of sorted) {
    const start = it._min, end = it._min + it._dur;
    const used = new Set(active.filter((a) => a.end > start).map((a) => a.col));
    let col = 0; while (used.has(col)) col++;
    res[it.id] = col;
    active.push({ end, col });
  }
  return res;
}

const pad = (n) => String(n).padStart(2, "0");
const dateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtMin = (min) => { const t = START_HOUR * 60 + min; return `${pad(Math.floor(t / 60))}:${pad(Math.round(t % 60))}`; };

export default function Pianificazione() {
  const navigate = useNavigate();
  const { hexOf, labelOf } = useStatuses();
  const [tickets, setTickets] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [day, setDay] = useState(() => new Date());
  const [drag, setDrag] = useState(null);
  const [dragTicket, setDragTicket] = useState(null);
  const [fCats, setFCats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hoverId, setHoverId] = useState(null);
  const [showAllTechs, setShowAllTechs] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(iv); }, []);

  const load = useCallback(() => {
    const cur = dateStr(day);
    Promise.all([
      api.get("/tickets", { params: { intervention_date: cur, area: "ticket" } }),
      api.get("/tickets", { params: { unplanned: true, area: "ticket" } }),
    ]).then(([a, b]) => {
      const map = {};
      [...a.data, ...b.data].forEach((t) => { map[t.id] = t; });
      setTickets(Object.values(map));
    }).catch(() => {});
  }, [day]);
  useLiveRefresh(load);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/tecnici").then((r) => setTecnici(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const curDate = dateStr(day);
  const nowRaw = now.getHours() * 60 + now.getMinutes() - START_HOUR * 60;
  const nowMin = dateStr(now) === curDate && nowRaw >= 0 && nowRaw <= TOTAL_MIN ? nowRaw : null;
  const catMatch = (t) => fCats.length === 0 || fCats.includes(t.category);
  const toggleCat = (c) => setFCats((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const startMin = (t) => {
    const st = t.start_time || "09:00";
    const [h, m] = st.split(":").map(Number);
    return Math.max(0, Math.min(h * 60 + m - START_HOUR * 60, TOTAL_MIN - SNAP));
  };

  const scheduled = useMemo(() => tickets
    .filter((t) => t.intervention_date === curDate && catMatch(t))
    .map((t) => ({ id: t.id, ticket: t, techId: t.assigned_to || "none", min: startMin(t), dur: t.duration_min || 120 })),
    [tickets, curDate, fCats]);
  const busyTechs = new Set(scheduled.map((s) => s.techId));
  const rows = [...tecnici.filter((t) => showAllTechs || busyTechs.has(t.id)).map((t) => ({ id: t.id, name: t.name })), { id: "none", name: "Non assegnato" }];

  const daPianificare = tickets.filter((t) => !t.intervention_date && !["completato", "sospeso_manutenzione"].includes(t.status) && catMatch(t));

  const save = async (id, patch) => {
    setTickets((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try { await api.put(`/tickets/${id}`, patch); } catch (e) { toast.error(apiError(e)); load(); }
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dxMin = Math.round((e.clientX - drag.startX) / PX_PER_MIN / SNAP) * SNAP;
      if (drag.mode === "move") {
        const newMin = Math.max(0, Math.min(drag.min0 + dxMin, TOTAL_MIN - drag.dur));
        const dyRows = Math.round((e.clientY - drag.startY) / ROW_H);
        const idx = Math.max(0, Math.min(drag.techIdx0 + dyRows, rows.length - 1));
        setDrag((d) => ({ ...d, min: newMin, techId: rows[idx].id }));
      } else {
        const newDur = Math.max(SNAP, Math.min(drag.dur0 + dxMin, TOTAL_MIN - drag.min));
        setDrag((d) => ({ ...d, dur: newDur }));
      }
    };
    const onUp = () => {
      const moved = drag.mode === "resize" || drag.min !== drag.min0 || drag.dur !== drag.dur0 || drag.techId !== drag.techId0;
      if (!moved && drag.mode === "move") { setDrag(null); navigate(`/tickets/${drag.id}`, { state: { from: "/pianificazione" } }); return; }
      const total = START_HOUR * 60 + drag.min;
      const patch = { intervention_date: curDate, start_time: `${pad(Math.floor(total / 60))}:${pad(total % 60)}`, duration_min: drag.dur };
      if (drag.mode === "move" && drag.techId !== drag.techId0) patch.assigned_to = drag.techId === "none" ? "" : drag.techId;
      save(drag.id, patch);
      setDrag(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, [drag, curDate]); // eslint-disable-line

  const startDrag = (e, item, techIdx, mode) => {
    e.preventDefault(); e.stopPropagation();
    setDrag({ id: item.id, ticket: item.ticket, mode, startX: e.clientX, startY: e.clientY, min0: item.min, dur0: item.dur, min: item.min, dur: item.dur, techId: item.techId, techId0: item.techId, techIdx0: techIdx });
  };

  const pianifica = (t) => { save(t.id, { intervention_date: curDate, start_time: "09:00", duration_min: t.duration_min || 120 }); toast.success(`${t.code} pianificato alle 09:00`); };
  const shiftDay = (n) => { const d = new Date(day); d.setDate(d.getDate() + n); setDay(d); };

  const onLaneDrop = (e, techId) => {
    e.preventDefault();
    if (!dragTicket) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const dur = dragTicket.duration_min || 120;
    let min = Math.round(((e.clientX - rect.left) / PX_PER_MIN) / SNAP) * SNAP;
    min = Math.max(0, Math.min(min, TOTAL_MIN - dur));
    const total = START_HOUR * 60 + min;
    save(dragTicket.id, { intervention_date: curDate, start_time: `${pad(Math.floor(total / 60))}:${pad(total % 60)}`, duration_min: dur, assigned_to: techId === "none" ? "" : techId });
    toast.success(`${dragTicket.code} pianificato alle ${fmtMin(min)}`);
    setDragTicket(null);
  };

  const hours = []; for (let h = START_HOUR; h <= END_HOUR; h++) hours.push(h);
  const catColorOf = (name) => (categories.find((c) => c.name === name) || {}).color || CAT_COLOR[name] || "#64748B";
  const usedCats = [...new Set(scheduled.map((it) => it.ticket.category).filter(Boolean))];
  const usedStatuses = [...new Set(scheduled.map((it) => it.ticket.status).filter(Boolean))];

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader overline="Flux · Gestione Ticket" title="Pianificazione - Delivery & Assurance" subtitle="Timeline interventi per tecnico — trascina per spostare l'orario/tecnico, ridimensiona per la durata">
        {(usedCats.length > 0 || usedStatuses.length > 0) && (
          <div className="mr-auto flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="plan-legend">
            {usedCats.map((cat) => (
              <span key={cat} className="flex items-center gap-1.5 text-xs font-semibold text-navy" data-testid={`legend-${cat}`}>
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: catColorOf(cat) }} />{cat}
              </span>
            ))}
            {usedCats.length > 0 && usedStatuses.length > 0 && <span className="h-4 w-px bg-border" />}
            {usedStatuses.map((st) => (
              <span key={st} className="flex items-center gap-1.5 text-xs font-semibold text-navy" data-testid={`legend-status-${st}`}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: hexOf(st) }} />{labelOf(st)}
              </span>
            ))}
          </div>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="plan-filter-categoria"><Filter size={16} />Categoria{fCats.length > 0 && <span className="ml-1 rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground">{fCats.length}</span>}</Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-52 p-2">
            <div className="space-y-1">
              {categories.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-secondary">
                  <Checkbox checked={fCats.includes(c.name)} onCheckedChange={() => toggleCat(c.name)} data-testid={`plan-cat-${c.name}`} />
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}
                </label>
              ))}
              {fCats.length > 0 && <button onClick={() => setFCats([])} className="mt-1 w-full text-left text-xs font-semibold text-primary">Deseleziona tutto</button>}
            </div>
          </PopoverContent>
        </Popover>
        <Button variant={showAllTechs ? "default" : "outline"} onClick={() => setShowAllTechs((v) => !v)} data-testid="plan-toggle-all-techs"
          title="Mostra anche i tecnici senza ticket nel giorno, per assegnarli">
          <UserCircle2 size={16} />{showAllTechs ? "Tutti i tecnici" : "Solo con ticket"}
        </Button>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-1">
          <button onClick={() => shiftDay(-1)} className="rounded-md p-1.5 hover:bg-secondary" data-testid="plan-prev"><ChevronLeft size={18} /></button>
          <button onClick={() => setDay(new Date())} className="flex items-center gap-2 px-2 text-sm font-semibold text-navy" data-testid="plan-today"><CalendarDays size={16} />{day.toLocaleDateString("it-IT", { weekday: "short" })} {fmtDate(dateStr(day))}</button>
          <button onClick={() => shiftDay(1)} className="rounded-md p-1.5 hover:bg-secondary" data-testid="plan-next"><ChevronRight size={18} /></button>
        </div>
      </PageHeader>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex">
          <div className="w-44 shrink-0 border-r border-border">
            <div className="h-10 border-b border-border bg-secondary/40" />
            {rows.map((r) => (
              <div key={r.id} style={{ height: ROW_H }} className="flex items-center gap-2 border-b border-border/60 px-3 last:border-0">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-xs font-bold text-white">{r.id !== "none" ? r.name[0] : <UserCircle2 size={15} />}</span>
                <span className="truncate text-sm font-semibold text-navy">{r.name}</span>
              </div>
            ))}
          </div>
          <div className="flex-1 overflow-x-auto">
            <div style={{ width: LANE_W }} className="relative">
              {nowMin !== null && (
                <div className="pointer-events-none absolute bottom-0 top-0 z-[45] w-0.5 -translate-x-1/2 bg-red-500" style={{ left: nowMin * PX_PER_MIN }} data-testid="plan-now-line">
                  <span className="absolute left-1/2 top-1 -translate-x-1/2 whitespace-nowrap rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{fmtMin(nowMin)}</span>
                </div>
              )}
              <div className="flex h-10 border-b border-border bg-secondary/40">
                {hours.map((h) => <div key={h} style={{ width: 60 * PX_PER_MIN }} className="shrink-0 border-r border-border/40 px-2 py-2 text-xs font-bold text-muted-foreground">{pad(h)}:00</div>)}
              </div>
              <div className="relative">
                {rows.map((r, ri) => {
                  const laneItems = scheduled
                    .filter((it) => { const active = drag && drag.id === it.id; const tech = active && drag.mode === "move" ? drag.techId : it.techId; return tech === r.id; })
                    .map((it) => { const active = drag && drag.id === it.id; return { ...it, _min: active ? drag.min : it.min, _dur: active ? drag.dur : it.dur }; });
                  const cols = stackCols(laneItems);
                  const hasOverlap = Object.values(cols).some((c) => c > 0);
                  return (
                  <div key={r.id} data-testid={`lane-${r.name}`} style={{ height: ROW_H }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onLaneDrop(e, r.id)} className="relative border-b border-border/60 last:border-0">
                    {hours.map((h, i) => <div key={h} style={{ left: i * 60 * PX_PER_MIN }} className="absolute bottom-0 top-0 border-r border-border/30" />)}
                    {laneItems.map((it) => {
                      const active = drag && drag.id === it.id;
                      const min = it._min;
                      const dur = it._dur;
                      const col = cols[it.id] || 0;
                      const color = hexOf(it.ticket.status);
                      const expanded = !drag && hoverId === it.id && hasOverlap;
                      return (
                        <div key={it.id} data-testid={`tile-${it.ticket.code}`} onPointerDown={(e) => startDrag(e, it, ri, "move")}
                          onMouseEnter={() => setHoverId(it.id)} onMouseLeave={() => setHoverId((h) => (h === it.id ? null : h))}
                          style={{ left: min * PX_PER_MIN, width: dur * PX_PER_MIN, top: expanded ? 8 : 8 + col * BAND_H, height: expanded ? ROW_H - 16 : Math.max(ROW_H - 16 - col * BAND_H, BAND_H), zIndex: active ? 50 : (expanded ? 40 : col + 1), borderLeftColor: color, background: "#ffffff" }}
                          className={`absolute cursor-grab select-none overflow-hidden rounded-md border border-l-4 border-border py-1 pl-[36px] pr-2 shadow-sm transition-[top,height] duration-150 active:cursor-grabbing ${expanded ? "ring-2 ring-primary/40 shadow-lg" : ""}`}>
                          {it.ticket.category && (
                            <span data-testid={`tile-cat-${it.ticket.code}`} title={it.ticket.category}
                              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", background: catColorOf(it.ticket.category) }}
                              className="absolute bottom-0 left-0 top-0 flex w-[28px] items-center justify-center whitespace-normal break-words px-0.5 py-1 text-center text-[9px] font-bold uppercase leading-tight tracking-tight text-white">
                              {it.ticket.category}
                            </span>
                          )}
                          <p className="truncate font-mono text-[10px] font-bold" style={{ color }}>{it.ticket.code}{it.ticket.id_olo ? ` · OLO ${it.ticket.id_olo}` : ""}</p>
                          <p className="truncate text-xs font-semibold text-navy">{it.ticket.client_name}</p>
                          {it.ticket.appointment_time && (
                            <p className="mt-0.5 flex w-fit items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-bold text-primary" data-testid={`tile-appt-${it.ticket.code}`}>
                              <CalendarClock size={11} />App. {it.ticket.appointment_time}
                            </p>
                          )}
                          <p className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground"><Clock size={9} />{fmtMin(min)}–{fmtMin(min + dur)}</p>
                          {it.ticket.comune && <p className="flex items-center gap-1 truncate text-[10px] text-muted-foreground"><MapPin size={9} />{it.ticket.comune}</p>}
                          <div onPointerDown={(e) => startDrag(e, it, ri, "resize")} className="absolute right-0 top-0 h-full w-2 cursor-ew-resize" data-testid={`resize-${it.ticket.code}`} />
                        </div>
                      );
                    })}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-bold text-navy">Da pianificare</h3>
        <p className="mb-3 text-sm text-muted-foreground">Trascina un intervento sulla timeline oppure usa "Pianifica"</p>
        <div className="flex flex-wrap gap-2">
          {daPianificare.length ? daPianificare.map((t) => (
            <div key={t.id} data-testid={`unplanned-${t.code}`} draggable onDragStart={() => setDragTicket(t)} onDragEnd={() => setDragTicket(null)}
              className="flex cursor-grab items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 active:cursor-grabbing">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: CAT_COLOR[t.category] }} />
              <span className="font-mono text-xs font-bold text-primary">{t.code}</span>
              <span className="text-sm font-semibold text-navy">{t.client_name}</span>
              <StatoBadge stato={t.status} />
              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => pianifica(t)} data-testid={`plan-btn-${t.code}`}><Plus size={14} />Pianifica</Button>
            </div>
          )) : <p className="text-sm text-muted-foreground">Nessun intervento da pianificare</p>}
        </div>
      </div>
    </div>
  );
}
